import type { Locales } from '@/i18n/i18n-types';
import type { ChartGranularity } from '@/lib/chart-axis';
import { bucketMergeChartData } from '@/lib/hooks/use-chart-data';

/**
 * Share of the store's revenue that came through the app (BACK-04):
 * `app ÷ (app + web)` in 0–1, `null` when the store sold nothing (no base).
 */
export function appShare(
  appRevenue: number,
  webRevenue: number
): number | null {
  const total = appRevenue + webRevenue;
  return total > 0 ? appRevenue / total : null;
}

export type AppShareRow = {
  /** X category (bucket label). */
  month: string;
  /** Bucket start (api date). */
  date: string;
  /** App share of the bucket, `null` without sales (off the line). */
  share: number | null;
};

/**
 * App share per chart bucket from `/charts/revenue` (BACK-04): app ÷
 * (app + web) of each bucket; a bucket where app + web = 0 is `null`, so the
 * line skips it.
 */
export function appShareRows(
  points: Array<{
    date: string;
    grossRevenueUsd: number;
    webGrossRevenue: number;
  }>,
  granularity: ChartGranularity,
  locale: Locales
): AppShareRow[] {
  return bucketMergeChartData<
    (typeof points)[number],
    { app: number; web: number }
  >(
    points,
    granularity,
    locale,
    (point, current) => ({
      app: (current?.app ?? 0) + point.grossRevenueUsd,
      web: (current?.web ?? 0) + point.webGrossRevenue,
    }),
    (_iso, { app, web }) => ({ app, web })
  ).map((row) => ({
    month: row.month,
    date: String(row.date),
    share: appShare(Number(row.app), Number(row.web)),
  }));
}
